/** GetWosBasicR2 신규 구조(writer/recipients) → UI 호환 flat 객체 */
export function normalizeWosBasicR2(rows) {
  if (!rows?.length) return null;
  const raw = rows[0];
  if (raw?.type === 'Error') return raw;
  if (!raw?.writer) return raw;

  const w = raw.writer || {};
  return {
    ...raw,
    ...w,
    name: w.name ?? w.userNm ?? raw.name,
    position: w.position ?? w.posiNm ?? raw.position,
    createDate: w.createDate ?? raw.createDate,
    stopComment: w.stopComment ?? raw.stopComment,
    stopImagePath: w.stopImagePath ?? raw.stopImagePath,
    createId: w.createId ?? w.userId ?? raw.createId,
    updateId: raw.updateId ?? w.updateId,
    tel: w.tel ?? w.phone ?? raw.tel,
    depCd: raw.targetDepCd ?? w.depCd ?? raw.depCd,
    targetDepCd: raw.targetDepCd ?? w.targetDepCd ?? raw.depCd,
    authEmtYn: raw.authEmtYn ?? w.authEmtYn,
    gubun: raw.gubun ?? w.gubun,
    solveYn: raw.solveYn ?? w.solveYn,
    solveComment: raw.solveComment ?? w.solveComment,
    solveImagePath: raw.solveImagePath ?? w.solveImagePath,
    solverName: raw.solverName ?? w.solverName,
    solverPosition: raw.solverPosition ?? w.solverPosition,
    solverDepNmLast: raw.solverDepNmLast ?? w.solverDepNmLast,
    updateDate: raw.updateDate ?? w.updateDate,
    workStopCustCd: raw.workStopCustCd ?? raw.custCd ?? w.custCd,
    wosType: raw.wosType ?? raw.WosType,
    writer: raw.writer ?? w,
    receivingDepartments: raw.receivingDepartments ?? [],
    recipients: raw.recipients ?? [],
  };
}

/** FCM/멤버 대상 ID 추출 */
export function extractRecipientUserIds(detail) {
  if (!detail) return [];
  if (detail.recipients?.length) {
    return detail.recipients
      .map((r) => r.userId ?? r.id ?? r.stopId)
      .filter(Boolean);
  }
  return [];
}

/** GetInfo emtYn — 어느 부서든 비상연락망 등록 시 Y */
export function isEmtUser(userInfo) {
  return String(userInfo?.emtYn ?? '').toUpperCase() === 'Y';
}

/** userInfo.resumeApprovalYn — N이면 작업재개 승인 불가 */
export function hasUserResumeApprovalYn(userInfo) {
  return String(userInfo?.resumeApprovalYn ?? 'Y').toUpperCase() !== 'N';
}

export function getAuthEmtLabel(authEmtYn) {
  return String(authEmtYn ?? '').toUpperCase() === 'Y' ? '지시' : '보고';
}

/** 지시 / 보고 구분 */
export function getWosType(detail) {
  const wosType = detail?.wosType;
  if (wosType === '지시' || wosType === '보고') return wosType;
  return getAuthEmtLabel(detail?.authEmtYn);
}

/** 지시자 / 보고자 라벨 */
export function getWriterRoleLabel(detail) {
  return getWosType(detail) === '지시' ? '지시자' : '보고자';
}

/** 작업중지 상세(gubun=중지) 안내 문구 */
export function getWorkStopNoticeMessage(detail) {
  if (getWosType(detail) === '지시') {
    return {
      title: '작업을 즉시 중단하고 장비를 정지 바랍니다.',
      description:
        '위험요인을 제거할 때까지 비상대피 집결장소에서 대기해 주세요. 안전이 확보되면 작업 재개 여부 등을 안내드리겠습니다.',
    };
  }
  return {
    title: '작업 중지가 접수되었습니다.',
    description:
      '관리자는 즉시 현장을 확인하고 안전 조치를 취하시기 바랍니다.',
  };
}

/** 수신자를 부서명 기준으로 묶음 (부서 순서는 최초 등장 순) */
export function groupRecipientsByDepartment(recipients = []) {
  const groups = [];
  const indexByDep = new Map();

  for (const person of recipients) {
    const depNmLast = person?.depNmLast?.trim() || '미지정';
    if (!indexByDep.has(depNmLast)) {
      indexByDep.set(depNmLast, groups.length);
      groups.push({ depNmLast, members: [] });
    }
    groups[indexByDep.get(depNmLast)].members.push(person);
  }

  return groups;
}

/** 부서 · 직위 · 성명 한 줄 */
export function formatPersonLine(person) {
  if (!person) return '- · - · -';
  const dep = person.depNmLast?.trim() || '-';
  const pos = person.position?.trim() || '-';
  const name = person.name?.trim() || '-';
  return `${dep} · ${pos} · ${name}`;
}

/** 작성자(writer) 표시용 객체 */
export function getWriterPerson(detail) {
  if (!detail) return null;
  if (detail.writer) return detail.writer;
  return {
    depNmLast: detail.depNmLast ?? detail.writerDepNmLast,
    position: detail.position,
    name: detail.name,
  };
}
